import { useEffect, useState } from "react";
import { api, inr, fmtDate } from "@/lib/api";
import PageHeader, { EmptyState } from "@/components/PageHeader";
import { toast } from "sonner";
import RecordActions from "@/components/RecordActions";

import { Plus, ArrowUp, ArrowDown, Package } from "@phosphor-icons/react";
import ImportExportButtons from "@/components/ImportExportButtons";

export default function Inventory() {
  const [tab, setTab] = useState("materials");
  const [materials, setMaterials] = useState([]);
  const [fg, setFg] = useState([]);
  const [txns, setTxns] = useState([]);
  const [showMat, setShowMat] = useState(false);
  const [adjustFor, setAdjustFor] = useState(null);
  const [matForm, setMatForm] = useState({ code: "", name: "", category: "fabric", unit: "meter", current_stock: 0, reorder_level: 0, purchase_price: 0, gst_rate: 5 });
  const [adjust, setAdjust] = useState({ delta: 0, reason: "" });

  const load = async () => {
    try {
      const [m, f, t] = await Promise.all([
        api.get("/inventory/materials"),
        api.get("/inventory/finished-goods"),
        api.get("/inventory/transactions?limit=100"),
      ]);
      setMaterials(m.data); setFg(f.data); setTxns(t.data);
    } catch { toast.error("Could not load inventory"); }
  };
  useEffect(() => { load(); }, []);

  const submitMat = async (e) => {
    e.preventDefault();
    try { await api.post("/inventory/materials", matForm); toast.success("Material added"); setShowMat(false); load(); }
    catch (e) { toast.error("Failed to add"); }
  };

  const submitAdjust = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/inventory/materials/${adjustFor._id}/adjust`, { delta: Number(adjust.delta), reason: adjust.reason });
      toast.success("Stock adjusted"); setAdjustFor(null); setAdjust({ delta: 0, reason: "" }); load();
    } catch (e) { toast.error("Adjustment failed"); }
  };

  const TABS = [["materials", "Raw materials"], ["finished", "Finished goods"], ["transactions", "Transactions"]];

  return (
    <div data-testid="inventory-page" className="page-fade">
      <PageHeader eyebrow="Module" title="Inventory" description="Raw materials, finished goods and full transaction ledger — reconciled with every dispatch and receipt." tint="gold" icon={Package} actions={
        <>
          <ImportExportButtons entity="materials" onImported={load}/>
          {tab === "materials" && (
            <button className="btn-primary" onClick={() => setShowMat(true)} data-testid="new-material-btn"><Plus size={14}/> New material</button>
          )}
        </>
      }/>
      <div className="tab-list mb-6" data-testid="inv-tabs">
        {TABS.map(([t, l]) => (
          <button key={t} onClick={() => setTab(t)} className={tab === t ? "is-active" : ""} data-testid={`inv-tab-${t}`}>{l}</button>
        ))}
      </div>

      {tab === "materials" && (
        <div className="card-soft p-0 overflow-x-auto">
          <table className="table-modern">
            <thead><tr>
              <th>Code</th><th>Name</th><th>Category</th><th>Available</th><th>Reserved</th><th>Reorder</th><th>Unit cost</th><th></th>
            </tr></thead>
            <tbody>
              {materials.map((m) => {
                const avail = (m.current_stock || 0) - (m.reserved_stock || 0);
                const low = avail <= (m.reorder_level || 0);
                return (
                  <tr key={m._id} className={low ? "text-destructive" : ""} data-testid={`mat-row-${m.code}`}>
                    <td className="font-mono text-xs">{m.code}</td>
                    <td className="font-medium">{m.name}</td>
                    <td className="text-sm text-muted-foreground capitalize">{m.category}</td>
                    <td className="tabular">{avail} <span className="text-muted-foreground text-xs">{m.unit}</span></td>
                    <td className="tabular text-muted-foreground">{m.reserved_stock || 0}</td>
                    <td className="tabular">{m.reorder_level || 0}</td>
                    <td className="tabular">{inr(m.average_cost || m.purchase_price)}</td>
                    <td className="text-right whitespace-nowrap"><button onClick={() => setAdjustFor(m)} className="btn-ghost btn-sm" data-testid={`mat-adjust-${m.code}`}>Adjust</button><RecordActions entity="materials" record={m} frozen={["code"]} onChanged={load} /></td>
                  </tr>
                );
              })}
              {!materials.length && <tr><td colSpan={8}><EmptyState icon={Package} title="No materials yet" description="Add raw materials to start tracking cost, reserved and available stock." /></td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "finished" && (
        <div className="card-soft p-0 overflow-x-auto">
          <table className="table-modern">
            <thead><tr><th>SKU</th><th>Size</th><th>Colour</th><th>Available</th><th>Reserved</th><th>Sold</th><th>Warehouse</th><th>Cost</th><th></th></tr></thead>
            <tbody>
              {fg.map((f) => (
                <tr key={f._id} data-testid={`fg-row-${f.sku}`}>
                  <td className="font-mono text-xs">{f.sku}</td>
                  <td>{f.size}</td>
                  <td>{f.colour}</td>
                  <td className="tabular">{f.quantity_available}</td>
                  <td className="tabular text-muted-foreground">{f.quantity_reserved}</td>
                  <td className="tabular">{f.quantity_sold}</td>
                  <td className="text-sm text-muted-foreground">{f.warehouse}</td>
                  <td className="tabular">{inr(f.cost)}</td>
                  <td className="text-right whitespace-nowrap"><RecordActions entity="finished_goods" record={f} frozen={["sku"]} onChanged={load} /></td>
                </tr>
              ))}
              {!fg.length && <tr><td colSpan={8}><EmptyState icon={Package} title="No finished goods" description="Finished goods appear here once production is completed." /></td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "transactions" && (
        <div className="card-soft p-0 overflow-x-auto">
          <table className="table-modern">
            <thead><tr><th>Type</th><th>Ref</th><th>Qty</th><th>By</th><th>When</th></tr></thead>
            <tbody>
              {txns.map((t) => (
                <tr key={t._id} data-testid={`txn-row-${t._id}`}>
                  <td><span className="status-chip chip-taupe">{(t.txn_type||"").replace(/_/g," ")}</span></td>
                  <td className="font-mono text-xs">{t.reference_type || "—"} · {t.product_sku || t.material_id || ""}</td>
                  <td className={`tabular ${t.quantity < 0 ? "text-destructive" : "text-[color:hsl(var(--success))]"}`}>
                    {t.quantity > 0 ? <ArrowUp size={12} className="inline"/> : <ArrowDown size={12} className="inline"/>} {t.quantity}
                  </td>
                  <td className="text-sm text-muted-foreground">{t.performed_by || "system"}</td>
                  <td className="text-xs text-muted-foreground">{fmtDate(t.created_at)}</td>
                </tr>
              ))}
              {!txns.length && <tr><td colSpan={5}><EmptyState icon={Package} title="No transactions yet" description="Stock ins & outs will show here." /></td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {showMat && (
        <div className="fixed inset-0 z-50 modal-backdrop flex items-center justify-center p-4 fade-in" onClick={() => setShowMat(false)}>
          <form onSubmit={submitMat} onClick={(e)=>e.stopPropagation()} className="modal-surface modal-scale p-7 w-full max-w-lg" data-testid="new-material-form">
            <div className="label-eyebrow">New raw material</div>
            <div className="display-3 mt-1 mb-6">Create</div>
            <div className="grid grid-cols-2 gap-5">
              {["code","name"].map((k) => (
                <label key={k} className="block">
                  <span className="label-eyebrow capitalize">{k}</span>
                  <input required value={matForm[k]} onChange={(e)=>setMatForm({...matForm,[k]:e.target.value})}
                         className="input-underline" data-testid={`mat-input-${k}`}/>
                </label>
              ))}
              <label className="block">
                <span className="label-eyebrow">Category</span>
                <select value={matForm.category} onChange={(e)=>setMatForm({...matForm,category:e.target.value})}
                        className="input-soft mt-2" data-testid="mat-input-category">
                  {["fabric","lining","denim","cotton","lace","thread","zippers","buttons","labels","tags","packaging","jewellery_components","beads","stones","chains","bag_hardware","footwear_material","heels","soles","adhesives","other"].map(c=><option key={c} value={c}>{c}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="label-eyebrow">Unit</span>
                <input value={matForm.unit} onChange={(e)=>setMatForm({...matForm,unit:e.target.value})}
                       className="input-underline" data-testid="mat-input-unit"/>
              </label>
              <label className="block">
                <span className="label-eyebrow">Opening stock</span>
                <input type="number" step="0.01" value={matForm.current_stock} onChange={(e)=>setMatForm({...matForm,current_stock:Number(e.target.value)})}
                       className="input-underline" data-testid="mat-input-stock"/>
              </label>
              <label className="block">
                <span className="label-eyebrow">Reorder level</span>
                <input type="number" value={matForm.reorder_level} onChange={(e)=>setMatForm({...matForm,reorder_level:Number(e.target.value)})}
                       className="input-underline" data-testid="mat-input-reorder"/>
              </label>
              <label className="block">
                <span className="label-eyebrow">Purchase price</span>
                <input type="number" step="0.01" value={matForm.purchase_price} onChange={(e)=>setMatForm({...matForm,purchase_price:Number(e.target.value)})}
                       className="input-underline" data-testid="mat-input-price"/>
              </label>
              <label className="block">
                <span className="label-eyebrow">GST %</span>
                <input type="number" value={matForm.gst_rate} onChange={(e)=>setMatForm({...matForm,gst_rate:Number(e.target.value)})}
                       className="input-underline" data-testid="mat-input-gst"/>
              </label>
            </div>
            <div className="flex gap-2 mt-8">
              <button type="button" onClick={()=>setShowMat(false)} className="btn-ghost flex-1">Cancel</button>
              <button type="submit" className="btn-primary flex-1" data-testid="mat-submit">Create material</button>
            </div>
          </form>
        </div>
      )}

      {adjustFor && (
        <div className="fixed inset-0 z-50 modal-backdrop flex items-center justify-center p-4 fade-in" onClick={()=>setAdjustFor(null)}>
          <form onSubmit={submitAdjust} onClick={(e)=>e.stopPropagation()} className="modal-surface modal-scale p-7 w-full max-w-md" data-testid="mat-adjust-form">
            <div className="label-eyebrow">Adjust stock</div>
            <div className="display-3 mt-1 mb-6">{adjustFor.name}</div>
            <label className="block">
              <span className="label-eyebrow">Delta (negative to reduce)</span>
              <input required type="number" step="0.01" value={adjust.delta} onChange={(e)=>setAdjust({...adjust,delta:e.target.value})}
                     className="input-underline font-mono" data-testid="mat-adjust-delta"/>
            </label>
            <label className="block mt-5">
              <span className="label-eyebrow">Reason</span>
              <input required value={adjust.reason} onChange={(e)=>setAdjust({...adjust,reason:e.target.value})}
                     className="input-underline" data-testid="mat-adjust-reason"/>
            </label>
            <div className="flex gap-2 mt-8">
              <button type="button" onClick={()=>setAdjustFor(null)} className="btn-ghost flex-1">Cancel</button>
              <button type="submit" className="btn-primary flex-1" data-testid="mat-adjust-submit">Apply adjustment</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
